/**
 * Resolving which account each generated calendar post belongs to.
 *
 * The generator returns slots carrying a `platform` but no account id, while
 * `finalize` requires a real `account_ids` entry per post (it validates them
 * against `accounts.id`). Sending nothing resolvable makes the whole wizard
 * fail at the last step, so resolution happens here, before the request.
 */

/**
 * Pick the account id for a single generated post.
 *
 * Order of preference: an id the generator supplied, then the single account
 * selected for that platform, then the only selected account overall. A
 * platform with several selected accounts is deliberately left unresolved —
 * guessing one would publish to the wrong place.
 *
 * @param {Object} post - Generated post ({ platform, account_id? }).
 * @param {Array<Object>} accounts - Selected accounts ({ id, social_type }).
 * @returns {number|string|null} The account id, or null when undecidable.
 */
export function resolveAccountIdForPost(post, accounts = []) {
  const direct = post?.account_id ?? post?.accountId;
  if (direct !== undefined && direct !== null && direct !== "") return direct;

  const known = accounts.filter((a) => a && a.id !== undefined && a.id !== null);
  if (known.length === 0) return null;

  const platform = (post?.platform || "").toLowerCase();
  if (platform) {
    const matches = known.filter(
      (a) => String(a.social_type || a.platform || "").toLowerCase() === platform,
    );
    if (matches.length === 1) return matches[0].id;
    if (matches.length > 1) return null;
  }

  return known.length === 1 ? known[0].id : null;
}

/**
 * Resolve every generated post, keeping the ones that cannot be decided.
 *
 * @param {Array<Object>} posts - Generated posts.
 * @param {Array<Object>} accounts - Selected accounts ({ id, social_type }).
 * @returns {{resolved: Array<Object>, unresolved: Array<Object>}} Posts with an
 *   `account_id`, and the posts that could not be resolved (with their index).
 */
export function resolveCalendarPostAccounts(posts = [], accounts = []) {
  const resolved = [];
  const unresolved = [];

  posts.forEach((post, index) => {
    const accountId = resolveAccountIdForPost(post, accounts);
    if (accountId === null) {
      unresolved.push({ index, post });
      return;
    }
    resolved.push({ ...post, account_id: accountId });
  });

  return { resolved, unresolved };
}

/**
 * Short, human-readable label for an unresolved slot, for error messages.
 *
 * @param {Object} entry - Entry from `unresolved`.
 * @returns {string} Label such as "instagram 2026-07-30 10:00".
 */
export function describeUnresolvedSlot({ index, post } = {}) {
  const parts = [
    post?.platform,
    post?.scheduled_date,
    post?.scheduled_time,
  ].filter(Boolean);
  return parts.length ? parts.join(" ") : `#${(index ?? 0) + 1}`;
}
